import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, apiErrorDetail } from "@/lib/api";
import { PageHeader } from "./ui";
import PricingCards from "@/components/PricingCards";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CalendarClock } from "lucide-react";

export default function Billing() {
    const { t, lang } = useI18n();
    const { salon, refreshMe } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);

    const sub = salon?.subscription || {};

    useEffect(() => {
        api.get("/billing/history").then(({ data }) => setHistory(data)).catch(() => {});
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(loc.search);
        const sid = params.get("session_id");
        if (!sid) return;
        let tries = 0;
        const poll = async () => {
            tries++;
            try {
                const { data } = await api.get(`/billing/status/${sid}`);
                if (data.payment_status === "paid") { toast.success(t("common.saved")); await refreshMe(); nav("/app/billing", { replace: true }); return; }
                if (data.payment_status === "expired" || tries > 10) { nav("/app/billing", { replace: true }); return; }
            } catch { /* keep trying */ }
            setTimeout(poll, 2000);
        };
        poll();
    }, [loc.search]);

    const selectPlan = async (plan) => {
        if (plan === sub.plan && sub.status === "active") return;
        setBusy(true);
        try {
            // If already active, switching (upgrade/downgrade) uses change-plan
            if (sub.status === "active") {
                await api.post("/billing/change-plan", { plan });
                await refreshMe();
                toast.success(t("common.saved"));
            } else {
                const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
                window.location.href = data.checkout_url;
            }
        } catch (e) {
            const d = apiErrorDetail(e);
            if (d?.code === "too_many_active_employees") toast.error(t("billing.downgradeWarn"));
            else if (e?.response?.status === 503) toast.error(t("billing.notConfigured"));
            else toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        setBusy(true);
        try { await api.post("/billing/cancel"); await refreshMe(); toast.success(t("common.saved")); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    const statusLabel = { trial: t("billing.trial"), active: t("billing.active"), cancelled: t("billing.cancelled"), expired: t("billing.expired"), past_due: "Past due" }[sub.status] || sub.status;

    return (
        <div>
            <PageHeader title={t("billing.title")} />

            <div className="mb-8 rounded-2xl border border-border/70 bg-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="text-sm text-muted-foreground">{t("billing.currentPlan")}</p>
                        <p className="mt-1 font-serif text-2xl font-bold">
                            {sub.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : t("billing.trial")}
                            <span className="ml-3 rounded-full bg-accent/12 px-3 py-1 align-middle text-sm font-medium text-accent">{statusLabel}</span>
                        </p>
                        {sub.status === "trial" && (
                            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                <CalendarClock className="h-4 w-4" />{sub.days_remaining} {t("billing.daysRemaining")} · {t("billing.trialInfo")}
                            </p>
                        )}
                    </div>
                    {sub.status === "active" && (
                        <Button variant="outline" onClick={cancel} disabled={busy} className="rounded-full" data-testid="cancel-sub-btn">{t("billing.cancel")}</Button>
                    )}
                </div>
            </div>

            {busy && <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</div>}

            <h2 className="mb-4 font-semibold">{t("billing.choosePlan")}</h2>
            <PricingCards onSelect={selectPlan} currentPlan={sub.status === "active" ? sub.plan : null} />

            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-accent" />Stripe secure billing · {t("marketing.trialNote")}</p>
            <p data-testid="billing-payment-methods" className="mt-1 text-sm text-muted-foreground">{t("billing.paymentMethods")}</p>

            {history.length > 0 && (
                <div className="mt-10">
                    <h2 className="mb-4 font-semibold">{t("billing.history")}</h2>
                    <div className="overflow-hidden rounded-2xl border border-border/70">
                        <table className="w-full text-sm">
                            <tbody>
                                {history.map((h) => (
                                    <tr key={h.session_id} className="border-b border-border/60 last:border-0">
                                        <td className="px-4 py-3">{new Date(h.created_at).toLocaleDateString(lang)}</td>
                                        <td className="px-4 py-3 capitalize">{h.plan}</td>
                                        <td className="px-4 py-3">€{h.amount?.toFixed(2)}</td>
                                        <td className="px-4 py-3"><span className="capitalize">{h.payment_status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
